
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { X, Settings, Trash2, Plus } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface NodePanelProps {
  selectedNode: any;
  setSelectedNode: (node: any) => void;
  setNodes: any; // React Flow setNodes function
  onClose: () => void;
  isMobile: boolean;
}

export const NodePanel = ({ selectedNode, setSelectedNode, setNodes, onClose, isMobile }: NodePanelProps) => {
  // Node drag start handler
  const onDragStart = (event: React.DragEvent, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  // Update node data
  const updateNodeData = (key: string, value: any) => {
    if (!selectedNode) return;
    
    setNodes((nds: any[]) =>
      nds.map((node) => {
        if (node.id === selectedNode.id) {
          return {
            ...node,
            data: {
              ...node.data,
              [key]: value,
            },
          };
        }
        return node;
      })
    );
    
    // Update local selected node state
    setSelectedNode({
      ...selectedNode,
      data: {
        ...selectedNode.data,
        [key]: value,
      },
    });
  };
  
  // Delete the selected node
  const deleteNode = () => {
    if (!selectedNode) return;
    
    setNodes((nds: any[]) => nds.filter((node) => node.id !== selectedNode.id));
    setSelectedNode(null);
    if (isMobile) onClose();
  };

  return (
    <div className="h-full flex flex-col">
      <div className="border-b p-4 flex justify-between items-center">
        <h2 className="text-lg font-medium">
          {selectedNode ? 'Node Properties' : 'Add Nodes'}
        </h2>
        {isMobile && (
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>
      
      <ScrollArea className="flex-1 p-4">
        {selectedNode ? (
          <div className="space-y-4">
            <div>
              <Label htmlFor="node-label">Label</Label>
              <Input
                id="node-label"
                value={selectedNode.data.label || ''}
                onChange={(e) => updateNodeData('label', e.target.value)}
              />
            </div>
            
            {/* Node-specific properties */}
            {selectedNode.type === 'sharepoint' && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="list-name">SharePoint List</Label>
                  <Select 
                    value={selectedNode.data.listName || ''} 
                    onValueChange={(value) => updateNodeData('listName', value)}
                  >
                    <SelectTrigger id="list-name">
                      <SelectValue placeholder="Select a list" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="teamPolicies">Team Policies</SelectItem>
                      <SelectItem value="teamHealthChecks">Team Health Checks</SelectItem>
                      <SelectItem value="governanceMetrics">Governance Metrics</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="operation">Operation</Label>
                  <Select 
                    value={selectedNode.data.operation || ''} 
                    onValueChange={(value) => updateNodeData('operation', value)}
                  >
                    <SelectTrigger id="operation">
                      <SelectValue placeholder="Select operation" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="read">Read Items</SelectItem>
                      <SelectItem value="create">Create Item</SelectItem>
                      <SelectItem value="update">Update Item</SelectItem>
                      <SelectItem value="delete">Delete Item</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="filter">Filter (CAML)</Label>
                  <Input
                    id="filter"
                    value={selectedNode.data.filter || ''}
                    onChange={(e) => updateNodeData('filter', e.target.value)}
                    placeholder="<Eq><FieldRef Name='Title' /><Value Type='Text'>Example</Value></Eq>"
                  />
                </div>
              </div>
            )}
            
            {selectedNode.type === 'condition' && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="condition-field">Field</Label>
                  <Input
                    id="condition-field"
                    value={selectedNode.data.field || ''}
                    onChange={(e) => updateNodeData('field', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="condition-operator">Operator</Label>
                  <Select 
                    value={selectedNode.data.operator || ''} 
                    onValueChange={(value) => updateNodeData('operator', value)}
                  >
                    <SelectTrigger id="condition-operator">
                      <SelectValue placeholder="Select operator" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="equals">Equals</SelectItem>
                      <SelectItem value="notEquals">Not Equals</SelectItem>
                      <SelectItem value="contains">Contains</SelectItem>
                      <SelectItem value="greaterThan">Greater Than</SelectItem>
                      <SelectItem value="lessThan">Less Than</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="condition-value">Value</Label>
                  <Input
                    id="condition-value"
                    value={selectedNode.data.value || ''}
                    onChange={(e) => updateNodeData('value', e.target.value)}
                  />
                </div>
              </div>
            )}
            
            {selectedNode.type === 'action' && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="action-type">Action Type</Label>
                  <Select 
                    value={selectedNode.data.actionType || ''} 
                    onValueChange={(value) => updateNodeData('actionType', value)}
                  >
                    <SelectTrigger id="action-type">
                      <SelectValue placeholder="Select action type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="notification">Send Notification</SelectItem>
                      <SelectItem value="policyUpdate">Update Policy Status</SelectItem>
                      <SelectItem value="teamAction">Perform Team Action</SelectItem>
                      <SelectItem value="logActivity">Log Activity</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="action-target">Target</Label>
                  <Input
                    id="action-target"
                    value={selectedNode.data.target || ''}
                    onChange={(e) => updateNodeData('target', e.target.value)}
                    placeholder="User email or team ID"
                  />
                </div>
                <div>
                  <Label htmlFor="action-message">Message/Details</Label>
                  <Input
                    id="action-message"
                    value={selectedNode.data.message || ''}
                    onChange={(e) => updateNodeData('message', e.target.value)}
                  />
                </div>
              </div>
            )}
            
            {selectedNode.type === 'policyDetails' && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="policy-name">Policy Name</Label>
                  <Input
                    id="policy-name"
                    value={selectedNode.data.policyName || ''}
                    onChange={(e) => updateNodeData('policyName', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="policy-description">Description</Label>
                  <Input
                    id="policy-description"
                    value={selectedNode.data.description || ''}
                    onChange={(e) => updateNodeData('description', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="policy-category">Category</Label>
                  <Select 
                    value={selectedNode.data.category || ''} 
                    onValueChange={(value) => updateNodeData('category', value)}
                  >
                    <SelectTrigger id="policy-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="security">Security</SelectItem>
                      <SelectItem value="lifecycle">Lifecycle</SelectItem>
                      <SelectItem value="governance">Governance</SelectItem>
                      <SelectItem value="compliance">Compliance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="policy-active"
                    checked={selectedNode.data.isActive || false}
                    onCheckedChange={(checked) => updateNodeData('isActive', checked)}
                  />
                  <Label htmlFor="policy-active">Active</Label>
                </div>
              </div>
            )}
            
            <Button 
              variant="destructive" 
              onClick={deleteNode} 
              className="flex items-center gap-2 w-full mt-8"
            >
              <Trash2 className="h-4 w-4" />
              Delete Node
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Drag and drop nodes to create your policy flow. Click on a node to edit its properties.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Card 
                className="cursor-grab hover:shadow-md transition-shadow"
                draggable
                onDragStart={(event) => onDragStart(event, 'policyDetails')}
              >
                <CardHeader className="p-3">
                  <CardTitle className="text-sm">Policy Details</CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <CardDescription className="text-xs">
                    Define policy name, description, and settings
                  </CardDescription>
                </CardContent>
              </Card>
              
              <Card 
                className="cursor-grab hover:shadow-md transition-shadow" 
                draggable
                onDragStart={(event) => onDragStart(event, 'sharepoint')}
              >
                <CardHeader className="p-3">
                  <CardTitle className="text-sm">SharePoint Source</CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <CardDescription className="text-xs">
                    Connect to a SharePoint list or library
                  </CardDescription>
                </CardContent>
              </Card>
              
              <Card 
                className="cursor-grab hover:shadow-md transition-shadow" 
                draggable
                onDragStart={(event) => onDragStart(event, 'condition')}
              >
                <CardHeader className="p-3">
                  <CardTitle className="text-sm">Condition</CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <CardDescription className="text-xs">
                    Add logic conditions to your workflow
                  </CardDescription>
                </CardContent>
              </Card>
              
              <Card 
                className="cursor-grab hover:shadow-md transition-shadow" 
                draggable
                onDragStart={(event) => onDragStart(event, 'action')}
              >
                <CardHeader className="p-3">
                  <CardTitle className="text-sm">Action</CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <CardDescription className="text-xs">
                    Perform actions like notifications or updates
                  </CardDescription>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </ScrollArea>
    </div>
  );
};
